import { useEffect,useRef,useState,type FormEvent } from 'react';
import type { ShiftNotification,ShiftNotificationBox,ShiftVacation } from 'shared-models';
import { useShiftAuth } from '../../contexts/ShiftAuthProvider';
import { useShiftEvents } from '../../contexts/ShiftEventsProvider';
import { shiftNotificationsService } from '../../services/notifications/shift-notifications.service';
import { shiftVacationsService } from '../../services/vacations/shift-vacations.service';
import { mergeNotificationRows } from './notifications-page.utils';

export function useNotificationsPageController() {
  const {user}=useShiftAuth();const isManager=user?.role==='manager';
  const [box,setBox]=useState<ShiftNotificationBox>(isManager ? 'requests' : 'broadcasts');
  const [messages,setMessages]=useState<ShiftNotification[]>([]),[requests,setRequests]=useState<ShiftVacation[]>([]);
  const [body,setBody]=useState(''),[busy,setBusy]=useState(false),[loading,setLoading]=useState(false),[hasMore,setHasMore]=useState(false),[error,setError]=useState<string | null>(null);
  const generation=useRef(0),lifetime=useRef(0),pageLock=useRef(false),mutationLock=useRef(false),offset=useRef(0),cursor=useRef<string>(),requestId=useRef<string>();
  const liveMessages=useRef(new Map<number,ShiftNotification>()),liveRequests=useRef(new Map<number,ShiftVacation | null>());
  const load=async (append=false) => {
    if (append && pageLock.current) return;
    const requested=++generation.current;pageLock.current=true;setLoading(true);setError(null);
    liveMessages.current.clear();liveRequests.current.clear();
    const start=append ? offset.current : 0;
    try {
      if (box==='broadcasts') {
        const result=await shiftNotificationsService.list({offset:start,limit:20});if(requested!==generation.current) return;
        offset.current=start+result.messages.length;setHasMore(result.pagination.hasMore);
        const overlay=[...liveMessages.current.values()];
        setMessages(current => mergeNotificationRows(append ? current : [],[...result.messages,...overlay]));
      } else {
        const result=await shiftVacationsService.list({section:'pending',offset:start,limit:20,...(append && cursor.current ? {cursor:cursor.current} : {})});
        if(requested!==generation.current) return;
        offset.current=start+result.vacations.length;cursor.current=result.pagination?.nextCursor;setHasMore(result.pagination?.hasMore ?? false);
        const overlay=new Map(liveRequests.current);
        setRequests(current => {
          const rows=new Map([...(append ? current : []),...result.vacations].map(row => [row.id,row]));
          for(const [id,row] of overlay) {if(row) rows.set(id,row);else rows.delete(id);}
          return mergeNotificationRows([], [...rows.values()]).filter(row => row.status==='pending');
        });
      }
    } catch {if(requested===generation.current) setError('לא ניתן לטעון את ההתראות.');}
    finally {if(requested===generation.current) {pageLock.current=false;setLoading(false);}}
  };
  const ready=useShiftEvents(event => {
    if(event.type==='notification.upserted' && box==='broadcasts') {
      if(pageLock.current) liveMessages.current.set(event.notification.id,event.notification);
      setMessages(current => mergeNotificationRows(current,[event.notification]));
    } else if(event.type==='vacation.upserted' && box==='requests') {
      if(pageLock.current) liveRequests.current.set(event.vacation.id,event.vacation);
      setRequests(current => mergeNotificationRows(current,[event.vacation]).filter(row => row.status==='pending'));
    } else if(event.type==='vacation.removed' && box==='requests') {
      if(pageLock.current) liveRequests.current.set(event.id,null);
      setRequests(current => current.filter(row => row.id!==event.id));
    } else if(event.type==='sse.resync') void load();
  });
  useEffect(() => {
    let active=true;setMessages([]);setRequests([]);setHasMore(false);cursor.current=undefined;
    queueMicrotask(() => {if(active && ready) void load();});
    return () => {active=false;generation.current+=1;};
  },[box,ready,user?.id]);
  useEffect(() => () => {lifetime.current+=1;},[]);
  const mutate=async (action:() => Promise<unknown>,sent=false) => {
    if(mutationLock.current) return;mutationLock.current=true;setBusy(true);setError(null);const requested=lifetime.current;
    try {await action();if(sent && requested===lifetime.current) {setBody('');requestId.current=undefined;}}
    catch {if(requested===lifetime.current) setError('הפעולה נכשלה. ניתן לנסות שוב.');}
    finally {mutationLock.current=false;if(requested===lifetime.current) setBusy(false);}
  };
  const send=(event:FormEvent) => {
    event.preventDefault();if(!isManager || mutationLock.current || !body.trim()) return;
    requestId.current ??= crypto.randomUUID();
    void mutate(() => shiftNotificationsService.create({body:body.trim(),requestId:requestId.current!}),true);
  };
  return {box,isManager,messages,requests,body,busy,loading,hasMore,error,send,
    changeBox:(value:string | null) => {if(!mutationLock.current && (value==='requests' || value==='broadcasts')) setBox(value);},
    changeBody:(value:string) => {if(!mutationLock.current) {setBody(value);requestId.current=undefined;}},
    markRead:(message:ShiftNotification) => {if(!message.isRead) void mutate(() => shiftNotificationsService.markRead(message.id));},
    loadMore:() => {if(!mutationLock.current) void load(true);},refresh:() => {if(!mutationLock.current && !pageLock.current) void load();},
  };
}